import { npsFilters } from '@/net-promoter-system/constants/nps.constants.js';
import { NPS_COLOR_MAP } from '@/common/chart/chart-colors';

/**
 * Format an NPS-scale value (-100..100) for display.
 *
 * Returns a string so the integer / one-decimal distinction is preserved on render
 * (e.g. `47.0` is kept as `"47.0"` rather than collapsing to `"47"`).
 *
 * @param {number} value - Either a value already on the -100..100 scale, or a fraction in [-1, 1] when `fromFraction` is true.
 * @param {object} [options]
 * @param {boolean} [options.asDecimal=false] - When true, round to 1 decimal place; otherwise round to an integer.
 * @param {boolean} [options.fromFraction=false] - When true, multiply `value` by 100 before formatting.
 * @returns {string|*} Formatted score, or the original value if it isn't a number.
 */
export function formatNpsScore (value, { asDecimal = false, fromFraction = false } = {}) {
    if (typeof value !== 'number' || !Number.isFinite(value)) return value;
    const scaled = fromFraction ? value * 100 : value;

    return asDecimal ? scaled.toFixed(1) : `${Math.round(scaled)}`;
}

export function computeNpsMarginOfError ({ promoters, passive, detractors, npsScore }) {
    // https://www.genroe.com/blog/how-to-calculate-margin-of-error-and-other-stats-for-nps
    // https://www.dummies.com/education/math/statistics/checking-out-statistical-confidence-interval-critical-values/
    const totalResponses = detractors + passive + promoters;
    if (totalResponses === 0) {
        return -1;
    }
    // eslint-disable-next-line no-mixed-operators
    const promoterVariance = (1 - npsScore) ** 2 * (promoters / totalResponses);
    // eslint-disable-next-line no-mixed-operators
    const neutralVariance = (0 - npsScore) ** 2 * (passive / totalResponses);
    // eslint-disable-next-line no-mixed-operators
    const detractorVariance = (-1 - npsScore) ** 2 * (detractors / totalResponses);
    const variance = promoterVariance + neutralVariance + detractorVariance;
    const standardError = Math.sqrt(variance) / Math.sqrt(totalResponses);

    return standardError * 1.96;
}

export function computeResponseRate ({ visitorsSeen, numResponses }) {
    if (visitorsSeen === 0) {
        return 0;
    }

    return numResponses / visitorsSeen;
}

export function npsColorByScore (score) {
    if (score >= 9) {
        return NPS_COLOR_MAP.promoters;
    }

    if (score >= 7) {
        return NPS_COLOR_MAP.passive;
    }

    return NPS_COLOR_MAP.detractors;
}

const MS_PER_DAY = 86400000;
const DEFAULT_ELIGIBILITY_WINDOW_MS = 14 * MS_PER_DAY;

export function getViewsByRecurrenceEligibilityWindow (seenDates, eligibilityWindowMs = DEFAULT_ELIGIBILITY_WINDOW_MS) {
    if (!seenDates || !seenDates.length) return 0;

    const sortedDates = [...seenDates].sort((a, b) => a - b);
    const firstViewsArr = [];
    sortedDates.forEach((date) => {
        const lastIndex = firstViewsArr.length - 1;
        if (!firstViewsArr.length || date >= firstViewsArr[lastIndex] + eligibilityWindowMs) {
            firstViewsArr.push(date);
        }
    });

    return firstViewsArr.length;
}

export function getResponseGroupByScore (score) {
    const parsedScore = parseInt(score, 10);

    const ratingGroup = npsFilters.rating.find((group) =>
        group.items.some((item) => parseInt(item.id, 10) === parsedScore),
    );

    return ratingGroup?.responseGroup?.slice(0, -1) || {};
}
